import React, { useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import * as Styles from "./OnboardingParentWrapper.styles";
import { Steps } from "antd";
import Logo from "../../pages/onboarding/wrapper/Logo";
import backIcon from "assets/images/back-icon.svg";
import { NavigationProvider } from "../../context/NavigationContext";

const progressSteps = [
  {
    title: "Anamnese",
    description: "Wir erstellen deinen Ernährungsplan.",
  },
  {
    title: "Auswertung",
    description: "Du lernst deinen Ernährungsplan kennen.",
  },
  {
    title: "Start",
    description: "Du startest mit unserer Ernährungsplaner-App.",
  },
];

function OnboardingParentWrapper(props) {
  // Use useRef instead of useState to prevent re-renders
  const navigationMethodsRef = useRef({
    goBack: null,
    handleNextPage: null,
  });

  // Register callback that updates the ref directly
  const registerMethods = useCallback((methods) => {
    if (methods?.goBack && methods?.handleNextPage) {
      navigationMethodsRef.current = methods;
    }
  }, []);

  // Context value with memoization
  const navigationContextValue = useMemo(
    () => ({
      registerMethods,
    }),
    [registerMethods]
  );

  // Handler functions that use the ref
  const handleBack = (e) => {
    e && e.preventDefault();
    if (navigationMethodsRef.current?.goBack) {
      navigationMethodsRef.current.goBack();
    } else {
      document.dispatchEvent(new CustomEvent("onboarding:back"));
    }
  };

  const handleNext = () => {
    if (navigationMethodsRef.current?.handleNextPage) {
      navigationMethodsRef.current.handleNextPage();
    } else {
      document.dispatchEvent(new CustomEvent("onboarding:next"));
    }
  };

  // Determine class name
  const className =
    props.children?.type?.name === "Start" ||
    props.children?.type?.name === "Ernahrungsform" ||
    props.children?.type?.name === "Teilnehmer"
      ? "wrapper-content no-box-shadow"
      : "wrapper-content";

  const showBackButton = props.noBackButton ? false : true;

  return (
    <NavigationProvider value={navigationContextValue}>
      <Styles.OnboardingWrapperContainer>
        <MobileProgressNav progressStep={props.progressStep} />
        <DesktopProgressNav progressStep={props.progressStep} />

        {/* Inline main content */}
        <div className={className}>
          <div className="step-header">
            <div className="header-content">
              {showBackButton && (
                <Link
                  onClick={handleBack}
                  style={{
                    ...(props.children?.props?.title ? {} : { marginTop: 10 }),
                  }}
                >
                  <img src={backIcon} alt="back icon" />
                </Link>
              )}
              <div>
                <div className="headline">{props?.title}</div>
                <div
                  className="sub-headline"
                  style={{
                    ...(props?.title ? {} : { marginTop: 5 }),
                  }}
                >
                  {props?.subTitle}
                </div>
              </div>
            </div>
            <div className="actions">
              <span onClick={handleNext} className="main-button">
                {props?.btnText}
              </span>
            </div>
          </div>

          {/* Simply pass currentPath and progressStep to children */}
          {React.isValidElement(props.children)
            ? React.cloneElement(props.children, {
                currentPath: props.currentPath,
                progressStep: props.progressStep,
              })
            : props.children}
        </div>
      </Styles.OnboardingWrapperContainer>
    </NavigationProvider>
  );
}

function MobileProgressNav({ progressStep }) {
  return (
    <div className="wrapper-mobile-progress">
      <div className="logo">
        <Logo />
      </div>
      <div className="mobile-steps-header">
        <span>Schritte</span>
      </div>
      <div className="mobile-steps">
        <Steps
          size="small"
          type="inline"
          current={progressStep}
          items={progressSteps}
        />
      </div>
    </div>
  );
}

export function DesktopProgressNav({ progressStep }) {
  return (
    <div className="wrapper-desktop-progress">
      <div className="logo">
        <Logo />
      </div>
      <span className="desktop-progress-headline">
        Herzlich Willkommen zum Erstgespräch. 👋
      </span>
      <div className="desktop-steps-header">Schritte</div>
      <div className="desktop-steps">
        <Steps
          direction="vertical"
          type="vertical"
          current={progressStep}
          items={progressSteps}
        />
      </div>
    </div>
  );
}

export default OnboardingParentWrapper;
